import React, { useState, useCallback } from 'react';
import { Plus, Edit3, Trash2, X, AlertCircle } from 'lucide-react';

// 📝 COMMENT: Custom Field interface - matches backend structure exactly
export interface CustomField {
  name: string;
  type: string; // "text", "number", "amount", "date", "selection", "location"
  value: string;
  isRequired: boolean;
  fieldOptions?: string[]; // For selection types
}

interface CustomFieldsBuilderProps {
  customFields: CustomField[];
  onChange: (fields: CustomField[]) => void;
  color?: string;
}

// 📝 COMMENT: Field types supported for custom fields
const CUSTOM_FIELD_TYPES = [
  { name: 'Text', value: 'text' },
  { name: 'Number', value: 'number' },
  { name: 'Amount', value: 'amount' },
  { name: 'Date', value: 'date' },
  { name: 'Location', value: 'location' },
  { name: 'Selection (Dropdown)', value: 'selection' }
];

/**
 * CustomFieldsBuilder Component
 *
 * A reusable component for managing custom fields in activityType and taskType.
 * Provides CRUD operations for custom fields with support for different field types.
 */
const CustomFieldsBuilder: React.FC<CustomFieldsBuilderProps> = ({
  customFields,
  onChange,
  color = '#3b82f6'
}) => {
  // 📝 COMMENT: State for add/edit custom field form
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [fieldFormData, setFieldFormData] = useState<CustomField>({
    name: '',
    type: 'text',
    value: '',
    isRequired: false,
    fieldOptions: []
  });
  const [fieldFormErrors, setFieldFormErrors] = useState<Record<string, string>>({});

  // 📝 COMMENT: State for managing field options (for selection type)
  const [optionText, setOptionText] = useState('');
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);

  // 📝 COMMENT: Validate custom field form
  const validateFieldForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!fieldFormData.name?.trim()) {
      errors.name = 'Field name is required';
    }

    if (!fieldFormData.type) {
      errors.type = 'Field type is required';
    }

    // Check for selection type options
    if (fieldFormData.type === 'selection' && (!fieldFormData.fieldOptions || fieldFormData.fieldOptions.length === 0)) {
      errors.fieldOptions = 'At least one option is required for selection fields';
    }

    // Check for duplicate field names
    const duplicateField = customFields.find(
      (field, index) =>
        field.name.toLowerCase() === fieldFormData.name.toLowerCase() &&
        (editingFieldIndex === null || index !== editingFieldIndex)
    );

    if (duplicateField) {
      errors.name = 'A field with this name already exists';
    }

    setFieldFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [fieldFormData, customFields, editingFieldIndex]);

  // 📝 COMMENT: Handle add/edit custom field
  const handleSaveField = useCallback(() => {
    if (!validateFieldForm()) {
      return;
    }

    const updatedFields = [...customFields];
    const fieldToSave = {
      ...fieldFormData,
      value: fieldFormData.name.trim().toLowerCase().replace(/\s+/g, '_')
    };

    if (editingFieldIndex !== null) {
      // Edit existing field
      updatedFields[editingFieldIndex] = fieldToSave;
    } else {
      // Add new field
      updatedFields.push(fieldToSave);
    }

    onChange(updatedFields);
    resetFieldForm();
  }, [fieldFormData, customFields, editingFieldIndex, onChange, validateFieldForm]);

  // 📝 COMMENT: Handle delete custom field
  const handleDeleteField = useCallback((index: number) => {
    const updatedFields = customFields.filter((_, i) => i !== index);
    onChange(updatedFields);
  }, [customFields, onChange]);

  // 📝 COMMENT: Handle edit custom field
  const handleEditField = useCallback((index: number) => {
    const field = customFields[index];
    setFieldFormData({ ...field });
    setEditingFieldIndex(index);
    setShowFieldForm(true);
  }, [customFields]);

  // 📝 COMMENT: Reset field form
  const resetFieldForm = useCallback(() => {
    setFieldFormData({
      name: '',
      type: 'text',
      value: '',
      isRequired: false,
      fieldOptions: []
    });
    setFieldFormErrors({});
    setEditingFieldIndex(null);
    setShowFieldForm(false);
    setOptionText('');
    setEditingOptionIndex(null);
  }, []);

  // 📝 COMMENT: Handle field options management (for selection type)
  const addOption = useCallback(() => {
    if (!optionText.trim()) return;

    const updatedOptions = [...(fieldFormData.fieldOptions || [])];

    if (editingOptionIndex !== null) {
      // Edit existing option
      updatedOptions[editingOptionIndex] = optionText.trim();
      setEditingOptionIndex(null);
    } else {
      // Add new option
      updatedOptions.push(optionText.trim());
    }

    setFieldFormData(prev => ({ ...prev, fieldOptions: updatedOptions }));
    setOptionText('');
  }, [optionText, editingOptionIndex, fieldFormData.fieldOptions]);

  const editOption = useCallback((index: number) => {
    const option = fieldFormData.fieldOptions?.[index];
    if (option) {
      setOptionText(option);
      setEditingOptionIndex(index);
    }
  }, [fieldFormData.fieldOptions]);

  const removeOption = useCallback((index: number) => {
    const updatedOptions = fieldFormData.fieldOptions?.filter((_, i) => i !== index) || [];
    setFieldFormData(prev => ({ ...prev, fieldOptions: updatedOptions }));
  }, [fieldFormData.fieldOptions]);

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <label style={{
          fontSize: '13px',
          fontWeight: '500',
          color: '#374151'
        }}>
          Custom Fields ({customFields.length})
        </label>

        {!showFieldForm && (
          <button
            type="button"
            onClick={() => setShowFieldForm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: color,
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            <Plus size={12} />
            Add Field
          </button>
        )}
      </div>

      {/* Custom Fields List */}
      {customFields.length > 0 && !showFieldForm && (
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '8px',
          marginBottom: '12px'
        }}>
          {customFields.map((field, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                marginBottom: index < customFields.length - 1 ? '6px' : 0
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '2px'
                }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {field.name}
                  </span>

                  <span style={{
                    fontSize: '10px',
                    fontWeight: '500',
                    color: '#6b7280',
                    backgroundColor: '#f3f4f6',
                    padding: '1px 4px',
                    borderRadius: '3px'
                  }}>
                    {CUSTOM_FIELD_TYPES.find(t => t.value === field.type)?.name || field.type}
                  </span>

                  {field.isRequired && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Required
                    </span>
                  )}
                </div>

                <div style={{
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  Value: {field.value}
                  {field.fieldOptions && field.fieldOptions.length > 0 &&
                    ` | Options: ${field.fieldOptions.join(', ')}`
                  }
                </div>
              </div>

              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => handleEditField(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  <Edit3 size={10} style={{ color: '#6b7280' }} />
                </button>

                <button
                  type="button"
                  onClick={() => handleDeleteField(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #fecaca',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  <Trash2 size={10} style={{ color: '#dc2626' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Field Form */}
      {showFieldForm && (
        <div style={{
          backgroundColor: '#ffffff',
          border: '2px solid ' + color,
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <h5 style={{
              margin: 0,
              fontSize: '13px',
              fontWeight: '600',
              color: '#111827'
            }}>
              {editingFieldIndex !== null ? 'Edit Custom Field' : 'Add Custom Field'}
            </h5>

            <button
              type="button"
              onClick={resetFieldForm}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '2px',
                color: '#6b7280'
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Field Name */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Field Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={fieldFormData.name}
              onChange={(e) => {
                setFieldFormData(prev => ({ ...prev, name: e.target.value }));
                if (fieldFormErrors.name) {
                  setFieldFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.name;
                    return newErrors;
                  });
                }
              }}
              placeholder="e.g., Payment Amount"
              style={{
                width: '100%',
                padding: '6px 8px',
                border: `1px solid ${fieldFormErrors.name ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '4px',
                fontSize: '12px',
                outline: 'none'
              }}
            />
            {fieldFormErrors.name && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
                fontSize: '10px',
                color: '#dc2626'
              }}>
                <AlertCircle size={10} />
                {fieldFormErrors.name}
              </div>
            )}
          </div>

          {/* Field Type */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Field Type <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <select
              value={fieldFormData.type}
              onChange={(e) => {
                setFieldFormData(prev => ({
                  ...prev,
                  type: e.target.value,
                  fieldOptions: e.target.value === 'selection' ? prev.fieldOptions : []
                }));
                if (fieldFormErrors.type) {
                  setFieldFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.type;
                    return newErrors;
                  });
                }
              }}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: `1px solid ${fieldFormErrors.type ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '4px',
                fontSize: '12px',
                outline: 'none'
              }}
            >
              {CUSTOM_FIELD_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.name}
                </option>
              ))}
            </select>
            {fieldFormErrors.type && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
                fontSize: '10px',
                color: '#dc2626'
              }}>
                <AlertCircle size={10} />
                {fieldFormErrors.type}
              </div>
            )}
          </div>

          {/* Field Options (for selection type) */}
          {fieldFormData.type === 'selection' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '12px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Options <span style={{ color: '#dc2626' }}>*</span>
              </label>

              {/* Add Option Input */}
              <div style={{
                display: 'flex',
                gap: '6px',
                marginBottom: '6px'
              }}>
                <input
                  type="text"
                  value={optionText}
                  onChange={(e) => setOptionText(e.target.value)}
                  placeholder="Enter option"
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '11px',
                    outline: 'none'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addOption();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={addOption}
                  disabled={!optionText.trim()}
                  style={{
                    backgroundColor: optionText.trim() ? color : '#d1d5db',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 10px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: optionText.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  {editingOptionIndex !== null ? 'Update' : 'Add'}
                </button>
              </div>

              {/* Options List */}
              {fieldFormData.fieldOptions && fieldFormData.fieldOptions.length > 0 && (
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '6px',
                  maxHeight: '100px',
                  overflowY: 'auto'
                }}>
                  {fieldFormData.fieldOptions.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '4px 6px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '3px',
                        marginBottom: index < fieldFormData.fieldOptions!.length - 1 ? '4px' : 0,
                        fontSize: '11px'
                      }}
                    >
                      <span style={{ flex: 1 }}>{option}</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          type="button"
                          onClick={() => editOption(index)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            color: '#6b7280'
                          }}
                        >
                          <Edit3 size={10} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            color: '#dc2626'
                          }}
                        >
                          <X size={10} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {fieldFormErrors.fieldOptions && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '4px',
                  fontSize: '10px',
                  color: '#dc2626'
                }}>
                  <AlertCircle size={10} />
                  {fieldFormErrors.fieldOptions}
                </div>
              )}
            </div>
          )}

          {/* Is Required Checkbox */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              color: '#374151'
            }}>
              <input
                type="checkbox"
                checked={fieldFormData.isRequired}
                onChange={(e) => setFieldFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
                style={{ width: '14px', height: '14px' }}
              />
              Mark as Required
            </label>
          </div>

          {/* Form Actions */}
          <div style={{
            display: 'flex',
            gap: '6px',
            justifyContent: 'flex-end',
            paddingTop: '8px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={resetFieldForm}
              style={{
                backgroundColor: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '11px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSaveField}
              style={{
                backgroundColor: color,
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '11px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              {editingFieldIndex !== null ? 'Update Field' : 'Add Field'}
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {customFields.length === 0 && !showFieldForm && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f9fafb',
          border: '1px dashed #d1d5db',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <p style={{
            margin: '0 0 8px 0',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            No custom fields added yet
          </p>
          <button
            type="button"
            onClick={() => setShowFieldForm(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: color,
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            <Plus size={12} />
            Add First Field
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsBuilder;
